/**
 * Telas da execução de Level Play e as transições **derivadas** de estado.
 *
 * Os valores são os mesmos que `journeys/Levels/config.js` e
 * `quizzes/Show/config.js` já declaravam — idênticos entre si, o que é parte da
 * razão de a máquina ser compartilhável sem adapter.
 *
 * Transições derivadas são as que o motor conclui sozinho a partir de corações
 * e índice de step. As **explícitas** — conclusão e tempo esgotado — continuam
 * sendo disparadas por quem as observa, e não passam por aqui.
 */

export const SCREENS = {
  INITIAL_SCREEN: 'initial',
  STEPS_SCREEN: 'steps',
  OUT_OF_TIME_SCREEN: 'out_of_time',
  OUT_OF_HEARTS_SCREEN: 'out_of_hearts',
  FINISH_SCREEN: 'finish'
} as const

export type Screen = (typeof SCREENS)[keyof typeof SCREENS]

export interface ScreenResolutionState {
  currentScreen: Screen | string | null
  hearts: number
  /** Índice do step atual, base zero. */
  currentStepIndex: number
}

/**
 * Tela para a qual o motor deve transicionar, ou `null` quando nada derivado se
 * aplica e a tela corrente permanece.
 *
 * A precedência é explícita porque a jornada resolvia isto em dois `useEffect`
 * independentes (`useLevel.ts`), onde a ordem de execução decidia o vencedor
 * quando ambos disparavam no mesmo render:
 *
 * 1. **Conclusão é terminal.** Nada derivado tira o participante dela.
 * 2. **Corações esgotados** vence o retorno à tela inicial — senão, quem zera
 *    os corações no primeiro step cairia na tela inicial em vez da de derrota.
 * 3. **Índice 0 volta à tela inicial**, já que o step de índice 0 é o
 *    `step-initial` de `getSteps`, que não é um desafio.
 *
 * As duas primeiras regras endurecem corridas que hoje não são alcançáveis (no
 * índice 0 não há desafio para errar, e não se conclui um nível com 0
 * corações). Estão aqui para que continuem inalcançáveis quando o motor passar
 * a servir dois domínios.
 */
export const resolveScreen = ({
  currentScreen,
  hearts,
  currentStepIndex
}: ScreenResolutionState): Screen | null => {
  if (currentScreen === SCREENS.FINISH_SCREEN) return null

  if (hearts <= 0) {
    return currentScreen === SCREENS.OUT_OF_HEARTS_SCREEN
      ? null
      : SCREENS.OUT_OF_HEARTS_SCREEN
  }

  if (currentStepIndex === 0) {
    // Tempo esgotado também é terminal para efeito do retorno à inicial: o
    // participante parou por tempo, não por estar no começo.
    if (currentScreen === SCREENS.OUT_OF_TIME_SCREEN) return null

    return currentScreen === SCREENS.INITIAL_SCREEN
      ? null
      : SCREENS.INITIAL_SCREEN
  }

  return null
}
